import { ReportBuilderActionTypes } from "./constants";
import {
  renameDuplicate,
  renameFieldDuplicate,
} from "../../utils/renameDuplicate";
import { FindFormIndex } from "../../utils/FindFormIndex";
import { updateFieldById } from "../../utils/updateFieldById";
import { applyFilterToChart, toLatinDigits } from "../../components/Reporter/helper";

interface Section {
  height: number;
  elements: any[];
}
type SectionKey = "header" | "detail" | "footer";
interface State {
  currentReportId: any;
  reportId: any | "";
  reportLocationId: any;

  sectionss: {
    header: Section;
    detail: Section;
    footer: Section;
  };
  filters: any[];
  previewFilters: { items: Record<string, any>; tags: any[] };
  config?: {
    publicSettings?: any;
    charts?: any[];
  };
}

const INIT_STATE = {
  currentReportId: "",
  reportId: "",
  reportLocationId: "",

  sectionss: {
    header: { height: 90, elements: [] },
    detail: { height: 380, elements: [] },
    footer: { height: 70, elements: [] },
  },
  filters: [],
  previewFilters: { items: {}, tags: [] },
  config: {
    publicSettings: {},
    charts: [],
    selected: null,
  },
};

// لیست تگ‌های قابل ارسال به بکند؛ همیشه از items مشتق می‌شود
const buildPreviewTags = (items: Record<string, any>) =>
  Object.values(items || {})
    .map((f: any) => {
      let value: string[] = (Array.isArray(f?.value) ? f.value : [])
        .filter((x: any) => x !== "" && x != null)
        .map(String);
      if (Number(f?.filterType) === 5) value = value.map(toLatinDigits);
      return {
        filterId: String(f.filterId),
        filterType: Number(f.filterType),
        isRange: !!f.isRange,
        value,
        sources: f.sources || [],
      };
    })
    .filter((t) => t.value.length > 0);

const ReportBuilder = (state: State = INIT_STATE, action: any): any => {
  switch (action.type) {
    case ReportBuilderActionTypes.CHANGE_REPORT_LOCATION:
      return {
        ...state,
        reportLocationId: action.payload,
      };

    case ReportBuilderActionTypes.CHANGE_CHARTS: {
      const items = Object.values(state.previewFilters?.items || {}) as any[];
      const charts = (action.payload || []).map((chart: any) =>
        items.reduce((acc, f) => {
          let v = (f.value || []).filter((x: any) => x !== "" && x != null).map(String);
          if (f.filterType === 5) v = v.map(toLatinDigits);
          return applyFilterToChart(acc, f.sources, v, !!f.isRange);
        }, chart)
      );
      return { ...state, config: { ...state.config, charts } };
    }

    case ReportBuilderActionTypes.UPDATE_ELEMENT: {
      const { sectionKey, id, updates } = action.payload;
      const section = state.sectionss[sectionKey as SectionKey];

      if (!section) return state;

      return {
        ...state,
        sectionss: {
          ...state.sectionss,
          [sectionKey]: {
            ...section,
            elements: section.elements.map((el: any) => {
              if (el.id !== id) return el;

              const merged = { ...el, ...updates };
              if (updates.props) {
                merged.props = { ...el.props, ...updates.props };
              }
              return merged;
            }),
          },
        },
      };
    }

    case ReportBuilderActionTypes.ADD_ELEMENT: {
      const { sectionKey, element } = action.payload as {
        sectionKey: SectionKey;
        element: any;
      };
      const section = state.sectionss[sectionKey];
      if (!section) return state;

      return {
        ...state,
        sectionss: {
          ...state.sectionss,
          [sectionKey]: {
            ...section,
            elements: [...section.elements, element],
          },
        },
      };
    }

    case ReportBuilderActionTypes.UPDATE_SECTION_HEIGHT: {
      const { sectionKey, height } = action.payload as {
        sectionKey: SectionKey;
        height: number;
      };
      const section = state.sectionss[sectionKey];
      if (!section) return state;

      return {
        ...state,
        sectionss: {
          ...state.sectionss,
          [sectionKey]: { ...section, height },
        },
      };
    }

    case ReportBuilderActionTypes.DELETE_ELEMENT: {
      const { sectionKey, id } = action.payload as {
        sectionKey: SectionKey;
        id: string;
      };
      const section = state.sectionss[sectionKey];
      if (!section) return state;

      return {
        ...state,
        sectionss: {
          ...state.sectionss,
          [sectionKey]: {
            ...section,
            elements: section.elements.filter((el) => el.id !== id),
          },
        },
      };
    }

    case ReportBuilderActionTypes.SET_SECTIONS:
      return {
        ...state,
        sectionss: action.payload,
      };

    case ReportBuilderActionTypes.SET_SELECTED:
      return {
        ...state,
        config: {
          ...state.config,
          selected: action.payload,
        },
      };

    case ReportBuilderActionTypes.ADD_FILTER:
      return {
        ...state,
        filters: [...(state.filters || []), action.payload],
      };

    case ReportBuilderActionTypes.UPDATE_FILTER: {
      const { id, updates } = action.payload;
      return {
        ...state,
        filters: (state.filters || []).map((f: any) =>
          f.id === id ? { ...f, ...updates } : f
        ),
      };
    }

    case ReportBuilderActionTypes.DELETE_FILTER: {
      const id = String(action.payload);
      const removed = state.previewFilters.items[id];
      const items = { ...state.previewFilters.items };
      delete items[id];

      const charts = removed
        ? (state.config?.charts || []).map((c: any) =>
            applyFilterToChart(c, removed.sources, [], false)
          )
        : state.config?.charts;

      return {
        ...state,
        filters: (state.filters || []).filter((f: any) => String(f.id) !== id),
        previewFilters: { items, tags: buildPreviewTags(items) },
        config: { ...state.config, charts },
      };
    }

    case ReportBuilderActionTypes.SET_FILTERS:
      return {
        ...state,
        filters: Array.isArray(action.payload) ? action.payload : [],
      };

    case ReportBuilderActionTypes.SET_PREVIEW_FILTER_VALUE: {
      const p = action.payload;
      const filterType = Number(p.filterType);
      const sources = Array.isArray(p.sources) ? p.sources : [];
      const isRange = !!p.isRange;

      let value: string[] = Array.isArray(p.value)
        ? p.value.filter((x: any) => x !== "" && x != null).map(String)
        : [];
      if (filterType === 5) value = value.map(toLatinDigits);

      const items = {
        ...state.previewFilters.items,
        [String(p.filterId)]: {
          filterId: String(p.filterId),
          title: p.title,
          filterType,
          value: Array.isArray(p.value) ? p.value : [], // مقدار خام برای نمایش در UI
          isRange,
          sources,
        },
      };

      return {
        ...state,
        previewFilters: { items, tags: buildPreviewTags(items) },
        config: {
          ...state.config,
          charts: (state.config?.charts || []).map((c: any) =>
            applyFilterToChart(c, sources, value, isRange)
          ),
        },
      };
    }

    case ReportBuilderActionTypes.RESET_PREVIEW_FILTERS:
      return { ...state, previewFilters: { items: {}, tags: [] } };

    default:
      return state;
  }
};

export default ReportBuilder;